// IntervalTree.ts
// A dynamic centered-interval tree for numeric intervals [low, high] (inclusive).
// Provides insert, remove, overlap search (range and point). Generic payload support.


export interface Interval<T> {
    low: number;
    high: number;
    value?: T;
}

type Entry<T> = Readonly<Interval<T>>;

class Node<T> {
    center: number;
    left: Node<T> | null = null;
    right: Node<T> | null = null;

    // Intervals stored at this node (all cross center)
    // byStart: sorted ascending by low (then high)
    // byEndDesc: sorted descending by high (then low)
    private byStart: Entry<T>[] = [];
    private byEndDesc: Entry<T>[] = [];

    constructor(center: number) {
        this.center = center;
    }

    insert(e: Entry<T>): void {
        if (e.high < this.center) {
            if (!this.left) this.left = new Node(midpoint(e.low, e.high));
            this.left.insert(e);
            return;
        }
        if (e.low > this.center) {
            if (!this.right) this.right = new Node(midpoint(e.low, e.high));
            this.right.insert(e);
            return;
        }
        // Store at this node (spans center)
        binaryInsert(this.byStart, e, cmpByStart);
        binaryInsert(this.byEndDesc, e, cmpByEndDesc);
    }

    remove(e: Entry<T>): boolean {
        if (e.high < this.center) {
            return this.left ? this.left.remove(e) : false;
        }
        if (e.low > this.center) {
            return this.right ? this.right.remove(e) : false;
        }
        const removedStart = removeFirst(this.byStart, e, eqEntry);
        const removedEnd = removeFirst(this.byEndDesc, e, eqEntry);
        return removedStart && removedEnd;
    }

    // Collects all intervals overlapping [ql, qh]
    searchRange(ql: number, qh: number, out: Entry<T>[]): void {
        if (ql <= this.center && qh >= this.center) {
            // Query spans center: all here match
            for (const e of this.byStart) out.push(e);
            if (this.left) this.left.searchRange(ql, qh, out);
            if (this.right) this.right.searchRange(ql, qh, out);
            return;
        }

        if (qh < this.center) {
            // Entirely left of center: scan byStart until low > qh
            for (const e of this.byStart) {
                if (e.low > qh) break;
                if (overlaps(e.low, e.high, ql, qh)) out.push(e);
            }
            if (this.left) this.left.searchRange(ql, qh, out);
            return;
        }

        // Entirely right of center: scan byEndDesc until high < ql
        for (const e of this.byEndDesc) {
            if (e.high < ql) break;
            if (overlaps(e.low, e.high, ql, qh)) out.push(e);
        }
        if (this.right) this.right.searchRange(ql, qh, out);
    }

    // True if any interval overlaps [ql, qh] (early-exit)
    hasOverlap(ql: number, qh: number): boolean {
        if (ql <= this.center && qh >= this.center) {
            if (this.byStart.length > 0) return true;
            return (this.left?.hasOverlap(ql, qh) ?? false) || (this.right?.hasOverlap(ql, qh) ?? false);
        }
        if (qh < this.center) {
            for (const e of this.byStart) {
                if (e.low > qh) break;
                if (overlaps(e.low, e.high, ql, qh)) return true;
            }
            return this.left?.hasOverlap(ql, qh) ?? false;
        }
        for (const e of this.byEndDesc) {
            if (e.high < ql) break;
            if (overlaps(e.low, e.high, ql, qh)) return true;
        }
        return this.right?.hasOverlap(ql, qh) ?? false;
    }

    // Point query specialized
    searchPoint(x: number, out: Entry<T>[]): void {
        if (x < this.center) {
            for (const e of this.byStart) {
                if (e.low > x) break;
                if (e.high >= x) out.push(e);
            }
            if (this.left) this.left.searchPoint(x, out);
            return;
        }
        if (x > this.center) {
            for (const e of this.byEndDesc) {
                if (e.high < x) break;
                if (e.low <= x) out.push(e);
            }
            if (this.right) this.right.searchPoint(x, out);
            return;
        }
        // x === center: all here match
        for (const e of this.byStart) out.push(e);
    }
}

/**
 * Complexity of operations:
 * - Insert: O(log n) average, O(n) worst-case (unbalanced)
 * - Remove: O(log n) average, O(n) worst-case (unbalanced)
 * - Search (range or point): O(k + log n) average, O(n) worst-case (unbalanced), where k is the number of results
 */
export class IntervalTree<T = unknown> {
    private root: Node<T> | null = null;
    private _size = 0;

    get size(): number {
        return this._size;
    }

    clear(): void {
        this.root = null;
        this._size = 0;
    }

    insert(low: number, high: number, value?: T): Interval<T> {
        const [l, h] = normalize(low, high);
        const entry: Entry<T> = Object.freeze({ low: l, high: h, value });
        if (!this.root) this.root = new Node(midpoint(l, h));
        this.root.insert(entry);
        this._size += 1;
        return entry;
    }

    remove(low: number, high: number, value?: T): boolean {
        if (!this.root) return false;
        const [l, h] = normalize(low, high);
        const entry: Entry<T> = { low: l, high: h, value };
        const ok = this.root.remove(entry);
        if (ok) this._size -= 1;
        return ok;
    }

    search(low: number, high: number): Interval<T>[] {
        if (!this.root) return [];
        const [l, h] = normalize(low, high);
        const out: Entry<T>[] = [];
        this.root.searchRange(l, h, out);
        // Return shallow copies to prevent external mutation of internal entries
        return out.map(cloneEntry);
    }

    searchPoint(x: number): Interval<T>[] {
        if (!Number.isFinite(x)) throw new Error("Point must be a finite number");
        if (!this.root) return [];
        const out: Entry<T>[] = [];
        this.root.searchPoint(x, out);
        return out.map(cloneEntry);
    }

    hasOverlap(low: number, high: number): boolean {
        if (!this.root) return false;
        const [l, h] = normalize(low, high);
        return this.root.hasOverlap(l, h);
    }
}

/* Helpers */

function overlaps(aL: number, aH: number, bL: number, bH: number): boolean {
    return aL <= bH && bL <= aH;
}

function normalize(a: number, b: number): [number, number] {
    if (!Number.isFinite(a) || !Number.isFinite(b)) throw new Error("Bounds must be finite numbers");
    return a <= b ? [a, b] : [b, a];
}

function midpoint(a: number, b: number): number {
    // Avoid overflow and keep it stable
    return a + (b - a) / 2;
}

function cloneEntry<T>(e: Entry<T>): Interval<T> {
    return { low: e.low, high: e.high, value: e.value };
}

function cmpByStart<T>(a: Entry<T>, b: Entry<T>): number {
    if (a.low !== b.low) return a.low - b.low;
    return a.high - b.high;
}

function cmpByEndDesc<T>(a: Entry<T>, b: Entry<T>): number {
    if (a.high !== b.high) return b.high - a.high; // descending
    return b.low - a.low;
}
function deepEqual(x: unknown, y: unknown): boolean {
    if (x === y) return true;
    if (x == null || y == null) return false;

    // Dates
    if (x instanceof Date && y instanceof Date) return x.getTime() === y.getTime();

    // Arrays
    if (Array.isArray(x) && Array.isArray(y)) {
        if (x.length !== y.length) return false;
        for (let i = 0; i < x.length; i++) if (!deepEqual(x[i], y[i])) return false;
        return true;
    }
    if (Array.isArray(x) || Array.isArray(y)) return false;

    // Plain objects
    if (typeof x === 'object' && typeof y === 'object') {
        const xObj = x as Record<string, unknown>;
        const yObj = y as Record<string, unknown>;
        const xKeys = Object.keys(xObj);
        const yKeys = Object.keys(yObj);
        if (xKeys.length !== yKeys.length) return false;
        for (const k of xKeys) {
            if (!Object.prototype.hasOwnProperty.call(yObj, k)) return false;
            if (!deepEqual(xObj[k], yObj[k])) return false;
        }
        return true;
    }

    // NaN case
    return Number.isNaN(x as number) && Number.isNaN(y as number);
}

function eqEntry<T>(a: Entry<T>, b: Entry<T>): boolean {
    return a.low === b.low && a.high === b.high && deepEqual(a.value, b.value);
}

function binaryInsert<T>(arr: T[], item: T, cmp: (a: T, b: T) => number): void {
    let lo = 0, hi = arr.length;
    while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (cmp(item, arr[mid]) <= 0) hi = mid;
        else lo = mid + 1;
    }
    arr.splice(lo, 0, item);
}

function removeFirst<T>(arr: T[], item: T, eq: (a: T, b: T) => boolean): boolean {
    const idx = findIndex(arr, item, eq);
    if (idx < 0) return false;
    arr.splice(idx, 1);
    return true;
}

function findIndex<T>(arr: T[], item: T, eq: (a: T, b: T) => boolean): number {
    for (let i = 0; i < arr.length; i++) if (eq(arr[i], item)) return i;
    return -1;
}